// KOA-5857 — Pure reducer for in-progress (unsaved) edits in the Table Editor.
//
// Kept side-effect-free so it can be unit-tested without React or jsdom.
// Composite-PK rows are addressed by a serialized RowPkString — see
// rowPkStringFromKey / rowPkStringFromRow below.

export type RowPkString = string;

// Column → cell-value map for one row's worth of changes. Defined here
// (rather than reusing IManagedDbRow from the service layer) so the pure
// reducer doesn't import service-layer types — the reducer file boasts
// about being React-free / service-free and we keep that property.
export interface IColumnValueMap {
  [columnName: string]: unknown;
}

export interface IPendingRowUpdate {
  pk: RowPkString;
  changes: IColumnValueMap;
  // Original values captured at the FIRST time each column was edited.
  // Subsequent edits of the same column preserve the original — that's
  // the value we send to the server for optimistic-concurrency checking.
  // Capturing at edit-time (not save-time) means a refresh / pagination
  // that moves the underlying row forward doesn't destroy our snapshot
  // of "what the user saw when they typed."
  originals: IColumnValueMap;
}

export interface IPendingRowInsert {
  clientId: string;
  values: IColumnValueMap;
}

export interface IPendingChanges {
  updates: Map<RowPkString, IPendingRowUpdate>;
  inserts: IPendingRowInsert[];
  deletes: Set<RowPkString>;
}

export type PendingChangesAction =
  // originalValue is the row's persisted value for this column at the
  // moment of the edit — captured once per (pk, column) and preserved
  // across subsequent edits. Caller threads it from the row data.
  | { type: "edit-cell"; pk: RowPkString; column: string; value: unknown; originalValue: unknown }
  // values? — when present (e.g. clone-row flow), seeds the new insert with
  // those column values. Identity/autoincrement columns should be stripped
  // by the caller; the reducer doesn't know which columns are auto-generated.
  | { type: "add-row"; clientId: string; values?: IColumnValueMap }
  | { type: "edit-insert-cell"; clientId: string; column: string; value: unknown }
  | { type: "remove-insert"; clientId: string }
  | { type: "delete-row"; pk: RowPkString }
  | { type: "undelete-row"; pk: RowPkString }
  // Drops successful mutations after a batch save. Pre-fix, the editor only
  // cleared pending state on full success — a partial failure left every
  // mutation dirty (including the ones that landed), so the user pressing
  // Save again would re-send already-applied inserts and trigger duplicate-
  // key errors. The reducer matches each successful result by clientId:
  // pk-keyed for updates/deletes, tmp-id for inserts.
  | { type: "clear-resolved"; resolved: IResolvedClientIds }
  | { type: "reset" };

// Compact form of the batch response — the reducer doesn't need the per-row
// error strings, only which clientIds succeeded. Caller decomposes the
// IManagedDbBatchResult[] into this shape so the reducer stays pure (no
// service-layer types).
export interface IResolvedClientIds {
  // Pk strings for resolved updates and deletes (same string space as the
  // Map / Set keys), plus tmp client-ids for resolved inserts. We do not
  // distinguish op type here because clientId values are unique across
  // updates / deletes / inserts.
  clientIds: string[];
}

export const emptyPendingChanges = (): IPendingChanges => {
  return {
    updates: new Map<RowPkString, IPendingRowUpdate>(),
    inserts: [],
    deletes: new Set<RowPkString>()
  };
};

const cloneUpdates = (src: Map<RowPkString, IPendingRowUpdate>): Map<RowPkString, IPendingRowUpdate> => {
  const next: Map<RowPkString, IPendingRowUpdate> = new Map();
  src.forEach((value: IPendingRowUpdate, key: RowPkString) => {
    next.set(key, { pk: value.pk, changes: { ...value.changes }, originals: { ...value.originals } });
  });
  return next;
};

// Cell-value equality for the no-op-revert detection. Plain === covers
// primitives, including the cross-Number-vs-string case where the
// backend ships Int64/Decimal as strings (so original "9007199254740993"
// stays a string round-trip; typing the same digits back lands a
// string identical via ===). null and undefined are explicitly
// collapsed: the grid shows both as "NULL" and the wire layer
// normalises them, so a revert from a non-null edit back to a NULL
// original (or vice-versa where the field never carried a value)
// shouldn't leave a dirty marker.
const sameOriginalValue = (a: unknown, b: unknown): boolean => {
  if (a === b) {
    return true;
  }
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  return false;
};

export const pendingChangesReducer = (state: IPendingChanges, action: PendingChangesAction): IPendingChanges => {
  switch (action.type) {
    case "edit-cell": {
      const updates: Map<RowPkString, IPendingRowUpdate> = cloneUpdates(state.updates);
      const existing: IPendingRowUpdate | undefined = updates.get(action.pk);
      // Capture the original value for this column ONCE — on the first
      // edit of (pk, column). Subsequent edits keep the original from the
      // first edit so a multi-edit cell still concurrency-checks against
      // what the user originally saw, not against their own in-progress
      // value. Using `in` so undefined originals (which are legitimate)
      // count as "already captured."
      const priorOriginals = existing?.originals ?? {};
      const capturedOriginal: unknown =
        action.column in priorOriginals ? priorOriginals[action.column] : action.originalValue;
      // No-op revert: if the new value matches the captured original
      // (the value the user saw before they touched the cell), the
      // edit isn't dirty any more. Drop the column from `changes` so
      // the cell stops rendering as dirty AND the Save batch doesn't
      // ship a redundant SET that wastes a round-trip and risks a
      // concurrency conflict that didn't need to exist. If dropping
      // the column empties the changes object, drop the whole row
      // update so the Save N counter is accurate.
      if (sameOriginalValue(action.value, capturedOriginal)) {
        if (!existing) {
          // Never had a pending change for this row — nothing to clear.
          return state;
        }
        if (!(action.column in existing.changes)) {
          // Row was dirty in OTHER columns; this column never landed
          // a change to begin with. State unchanged.
          return state;
        }
        const remainingChanges: IColumnValueMap = { ...existing.changes };
        delete remainingChanges[action.column];
        if (Object.keys(remainingChanges).length === 0) {
          // Last dirty column on the row reverted — drop the whole
          // update so dirtyCount / save batch don't carry a no-op.
          updates.delete(action.pk);
        } else {
          // Other columns still dirty. Strip just the reverted column;
          // keep the originals snapshot intact so re-editing later
          // doesn't lose the concurrency anchor.
          updates.set(action.pk, {
            pk: action.pk,
            changes: remainingChanges,
            originals: existing.originals
          });
        }
        return { updates, inserts: state.inserts, deletes: state.deletes };
      }
      const mergedOriginals: IColumnValueMap =
        action.column in priorOriginals ? priorOriginals : { ...priorOriginals, [action.column]: action.originalValue };
      const merged: IPendingRowUpdate = existing
        ? { pk: action.pk, changes: { ...existing.changes, [action.column]: action.value }, originals: mergedOriginals }
        : { pk: action.pk, changes: { [action.column]: action.value }, originals: mergedOriginals };
      updates.set(action.pk, merged);
      const deletes: Set<RowPkString> = new Set(state.deletes);
      deletes.delete(action.pk);
      return { updates, inserts: state.inserts, deletes };
    }
    case "add-row": {
      const inserts: IPendingRowInsert[] = [
        ...state.inserts,
        { clientId: action.clientId, values: action.values ? { ...action.values } : {} }
      ];
      return { updates: state.updates, inserts, deletes: state.deletes };
    }
    case "edit-insert-cell": {
      const inserts: IPendingRowInsert[] = state.inserts.map((row: IPendingRowInsert): IPendingRowInsert => {
        if (row.clientId !== action.clientId) {
          return row;
        }
        return { clientId: row.clientId, values: { ...row.values, [action.column]: action.value } };
      });
      return { updates: state.updates, inserts, deletes: state.deletes };
    }
    case "remove-insert": {
      const inserts: IPendingRowInsert[] = state.inserts.filter(
        (row: IPendingRowInsert): boolean => row.clientId !== action.clientId
      );
      return { updates: state.updates, inserts, deletes: state.deletes };
    }
    case "delete-row": {
      const deletes: Set<RowPkString> = new Set(state.deletes);
      deletes.add(action.pk);
      const updates: Map<RowPkString, IPendingRowUpdate> = cloneUpdates(state.updates);
      updates.delete(action.pk);
      return { updates, inserts: state.inserts, deletes };
    }
    case "undelete-row": {
      const deletes: Set<RowPkString> = new Set(state.deletes);
      deletes.delete(action.pk);
      return { updates: state.updates, inserts: state.inserts, deletes };
    }
    case "clear-resolved": {
      if (action.resolved.clientIds.length === 0) {
        return state;
      }
      const ids: Set<string> = new Set(action.resolved.clientIds);
      const updates: Map<RowPkString, IPendingRowUpdate> = cloneUpdates(state.updates);
      // Remove resolved updates (clientId IS the pk string for updates).
      ids.forEach((id: string): void => {
        updates.delete(id);
      });
      const deletes: Set<RowPkString> = new Set(state.deletes);
      ids.forEach((id: string): void => {
        deletes.delete(id);
      });
      const inserts: IPendingRowInsert[] = state.inserts.filter(
        (row: IPendingRowInsert): boolean => !ids.has(row.clientId)
      );
      return { updates, inserts, deletes };
    }
    case "reset": {
      return emptyPendingChanges();
    }
    default: {
      return state;
    }
  }
};

// A freshly added row with no edited cells is a draft row, not a database
// INSERT. It should stay visible for typing, but it must not enable Save
// or produce a backend mutation like `{ values: {} }`.
export const isPersistableInsert = (insert: IPendingRowInsert): boolean => {
  return Object.keys(insert.values).length > 0;
};

export const pendingChangesMutationCount = (state: IPendingChanges): number => {
  const insertCount: number = state.inserts.filter(isPersistableInsert).length;
  return state.updates.size + insertCount + state.deletes.size;
};

// Pending-changes are dirty iff at least one persistable mutation exists.
export const isDirtyChanges = (state: IPendingChanges): boolean => {
  return pendingChangesMutationCount(state) > 0;
};

// One (column, value) entry in a row's primary-key tuple. Used both as
// input (rowPkStringFromKey) and output (rowPkStringToKey) — keep the same
// shape so callers can round-trip without unwrapping.
export interface IRowPkKeyEntry {
  column: string;
  value: unknown;
}

// Same shape but with a string-typed value. The deserializer (rowPkStringToKey)
// only knows strings — the backend's NormalizeParameterValue coerces them
// to Guid / DateTime / int per column on the server side.
export interface IRowPkKeyEntryString {
  column: string;
  value: string;
}

// Serialized PK string used as Map key. \x1F (ASCII Unit Separator)
// separates PK-tuple entries; `=` separates column from value inside one
// entry. Three characters need escaping so user-defined column names or
// values containing them can't corrupt the round-trip: `\`, `=`, `\x1F`.
//   `\` → `\B`   `=` → `\E`   `\x1F` → `\U`
// Encoding replaces every occurrence in the source, so the post-encode
// string contains literal `=` and literal `\x1F` ONLY as structural
// separators added at join time. Splitting is therefore unambiguous, and
// decode is a single left-to-right regex replace (every `\` in encoded
// form is always followed by exactly one of B/E/U). Common-case values
// with no special chars survive unchanged, so `id=1` stays `id=1`.
const PK_TUPLE_SEPARATOR: string = "\x1F";
const PK_COLUMN_VALUE_SEPARATOR: string = "=";

const escapePkPart = (raw: string): string => {
  let out: string = "";
  for (let i: number = 0; i < raw.length; i += 1) {
    const ch: string = raw[i];
    if (ch === "\\") {
      out += "\\B";
    } else if (ch === "=") {
      out += "\\E";
    } else if (ch === "\x1F") {
      out += "\\U";
    } else {
      out += ch;
    }
  }
  return out;
};

const unescapePkPart = (encoded: string): string => {
  return encoded.replace(/\\([BEU])/g, (_match: string, code: string): string => {
    if (code === "B") {
      return "\\";
    }
    if (code === "E") {
      return "=";
    }
    return "\x1F";
  });
};

export const rowPkStringFromKey = (entries: IRowPkKeyEntry[]): RowPkString => {
  return entries
    .map((entry: IRowPkKeyEntry): string => {
      return `${escapePkPart(entry.column)}${PK_COLUMN_VALUE_SEPARATOR}${escapePkPart(String(entry.value))}`;
    })
    .join(PK_TUPLE_SEPARATOR);
};

export const rowPkStringFromRow = (row: IColumnValueMap, primaryKeyColumns: string[]): RowPkString => {
  return primaryKeyColumns
    .map((col: string): string => {
      return `${escapePkPart(col)}${PK_COLUMN_VALUE_SEPARATOR}${escapePkPart(String(row[col]))}`;
    })
    .join(PK_TUPLE_SEPARATOR);
};

// Inverse of rowPkStringFromRow / rowPkStringFromKey. Needed by the save
// path so it can build an UPDATE / DELETE key without having the source row
// in `rows` — pending edits survive pagination, so the row may not be on
// the current page when the user hits Save. Backend coerces the string back
// to Guid / DateTime / int per column via NormalizeParameterValue, so
// stringified PK values round-trip cleanly.
export const rowPkStringToKey = (pk: RowPkString): IRowPkKeyEntryString[] => {
  if (pk.length === 0) {
    return [];
  }
  return pk.split(PK_TUPLE_SEPARATOR).map((part: string): IRowPkKeyEntryString => {
    const eqIdx: number = part.indexOf(PK_COLUMN_VALUE_SEPARATOR);
    if (eqIdx < 0) {
      return { column: unescapePkPart(part), value: "" };
    }
    return {
      column: unescapePkPart(part.substring(0, eqIdx)),
      value: unescapePkPart(part.substring(eqIdx + 1))
    };
  });
};
